/**
 * F-GEO08 — Vehicle JSON-LD for the shopper's active EasySearch vehicle.
 *
 * Emits a schema.org/Vehicle structured-data block in <head> when the
 * shopper has chosen a vehicle in EasySearch. Designed for "does it fit my
 * {year} {make} {model}" LLM and search queries: crawlers that execute
 * JavaScript pick up the appended <script type="application/ld+json"> as if
 * it were server-rendered.
 *
 * EasySearch keeps the active selection on `window.easysearch`:
 *   - `curIndex`          "Make,Model,Year,Chassis," (",,,," while nothing is chosen)
 *   - `preSelectFitment`  saved selection as an array, e.g. ["BMW","M3","2022","G80 Saloon",""]
 *                         (the empty string until something is saved)
 *   - `preSelect`         the same, for the main widget
 * `currentTag` is NOT a vehicle: it is a "+"-joined product-tag filter.
 * The field order is Make, Model, Year, Chassis (docs/INTEGRATIONS.md).
 *
 * Lookup order, first non-empty wins: curIndex, preSelectFitment, preSelect.
 *
 * Idempotent: removes the block it injected before injecting a fresh one.
 * Safe to call on `easysearch_loaded` and whenever EasySearch saves a new
 * selection (`easysearch_params_saved`).
 */

const SCHEMA_ELEMENT_ID = 'autoid-garage-vehicle-schema';

/**
 * @typedef {object} VehicleFields
 * @property {string} make
 * @property {string=} model
 * @property {string=} year
 * @property {string=} chassis
 */

/**
 * Splits an EasySearch selection into Vehicle schema fields. Empty segments
 * (EasySearch's trailing comma, or ",,,," while nothing is chosen) are dropped.
 *
 * @param {string} tag
 * @returns {VehicleFields | null}
 */
function parseFitmentTag(tag) {
  if (typeof tag !== 'string') return null;
  const segments = tag.split(',').map((s) => s.trim()).filter(Boolean);
  if (!segments.length) return null;
  const [make, model, year, chassis] = segments;
  return { make, model, year, chassis };
}

/**
 * @param {VehicleFields} fields
 * @returns {object} schema.org/Vehicle compatible payload
 */
function buildVehicleSchema(fields) {
  const name = [fields.year, fields.make, fields.model, fields.chassis]
    .filter(Boolean)
    .join(' ');

  /** @type {Record<string, unknown>} */
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name,
  };

  if (fields.make) schema.brand = { '@type': 'Brand', name: fields.make };
  if (fields.model) schema.model = fields.model;
  if (fields.year) schema.modelDate = fields.year;
  if (fields.chassis) schema.vehicleConfiguration = fields.chassis;

  return schema;
}

/**
 * Reads the shopper's active EasySearch selection as "Make,Model,Year,Chassis".
 * Returns null when nothing is chosen.
 *
 * @returns {string | null}
 */
function readActiveFitmentTag() {
  const es = /** @type {Record<string, unknown>} */ (
    /** @type {unknown} */ (window.easysearch || {})
  );

  /** @type {string[]} */
  const candidates = [];
  if (typeof es.curIndex === 'string') candidates.push(es.curIndex);
  if (Array.isArray(es.preSelectFitment)) candidates.push(es.preSelectFitment.join(','));
  if (Array.isArray(es.preSelect)) candidates.push(es.preSelect.join(','));

  return candidates.find((value) => value.replace(/,/g, '').trim()) || null;
}

function removeExistingSchema() {
  const existing = document.getElementById(SCHEMA_ELEMENT_ID);
  if (existing) existing.remove();
}

function injectVehicleSchema() {
  removeExistingSchema();

  const tag = readActiveFitmentTag();
  if (!tag) return;

  const fields = parseFitmentTag(tag);
  if (!fields || !fields.make) return;

  const script = document.createElement('script');
  script.id = SCHEMA_ELEMENT_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(buildVehicleSchema(fields));
  document.head.appendChild(script);
}

document.addEventListener('easysearch_loaded', injectVehicleSchema);
document.addEventListener('easysearch_params_saved', injectVehicleSchema);

// If this script runs after `easysearch_loaded` already fired, inject now so
// the first paint is not missed.
if (typeof window.easysearch === 'object' && window.easysearch !== null) {
  injectVehicleSchema();
}
